//================================================================
//  Component: User Search RHF
//================================================================

//  Purpose: This is an input field that allows you to search all LL Users and select a user

//  Properties:
//    - dataField = {useReducer Field, string of the field name}
//    - required = {boolean, this determines if this field is required}
//    - register = {react hooks forms state, connects the field to the form state}
//    - setValue = {react hooks form state, programmatically sets the value for a field}
//    - clearErrors = {react hooks form state, clears the errors for the field}
//    - setError = {react hooks form state, programmatically sets the error for a field}
//    - errors = {react hooks form state, stores the errors for the field}

//  Example:
//    <UserSearchRHF
//      dataField={'financemanager'}
//      required={true}
//      register={register}
//      setValue={setValue}
//      clearErrors={clearErrors}
//      setError={setError}
//      errors={errors.financemanager}
//    ></UserSearchRHF>    

//================================================================


//Libraries
import React, { useReducer } from 'react';

//Contexts

//Components

//Functions
import QueryDocument from '../../Library/QueryDocument';

//Images

export default function UserSearchRHF({
    dataField,
    required,
    register,
    setValue,
    clearErrors,
    setError,
    errors
}) {

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'newUserInput': '',
            'queryMessage': '',
            'searchResults': []
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle search
    const handleSearch = (value) => {

        setSearchData({
            'searchResults': [],
            'newUserInput': value,
        })

        // Clear the previous inputs
        setValue(`${dataField}givenname`, '');
        setValue(`${dataField}surname`, '');

        // Validate input
        if (value?.length === 0) {
            // set error
            setError(dataField, {
                type: 'required',
                message: 'This field is required.'
            })
            return setSearchData({ 'queryMessage': 'Please enter an email address.' });

        }

        if (value?.length === 2) {
            return setSearchData({ 'queryMessage': 'Search requires more than two characters.' });

        }

        const query = value?.toLowerCase();

        // Search by characters in email
        return QueryDocument('users', [
            ['searcharray', 'array-contains-any', [query]],
            ['status', '==', 'active'],
            ['usertype', 'in', ['lendleaseuser', 'new', 'contractor']]
        ])
            .then((users) => {

                setSearchData({
                    'searchResults': users,
                    'queryMessage': `Showing ${users?.length} result(s)`
                });

            })
            .catch((error) => {

                console.log(error);
                setSearchData({ 'queryMessage': 'Oops something went wrong!' });

            });

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col w-full h-full relative'>

            {/* ------------------------------------------------------ */}
            {/*  Users Search Bar                                      */}
            {/* ------------------------------------------------------ */}

            {/* Search Bar */}
            <div className='flex flex-row items-center'>

                {/* Search Input Field */}
                <input
                    className={
                        errors ?
                            'w-full h-[42px] px-[15px] py-[10px] bg-[white] border-[1.5px] border-solid border-[#DE0000] rounded-[5px]'
                            : searchData?.searchResults?.length > 0 ?
                                'w-full h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-tl-[5px] rounded-br-none rounded-tr-[5px] rounded-bl-none'
                                :
                                'w-full h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-[5px]'
                    }
                    type='text'
                    placeholder='Search user by email address'
                    {...register(dataField, {
                        required: required ? 'This field is required' : false,
                    })}
                    onChange={(e) => handleSearch(e.target.value)}
                    onPaste={(e) => handleSearch(e.clipboardData.getData('Text'))}
                    autoComplete='off'
                ></input>

            </div>

            {/* Search Results */}
            {
                (
                    <div className='absolute w-full z-10 top-11 max-h-[150px] overflow-y-auto bg-white shadow-md rounded-tl-none rounded-br-[5px] rounded-tr-none rounded-bl-[5px] '>

                        {/* No Results */}
                        {
                            (searchData?.searchResults?.length === 0 && searchData?.newUserInput !== '' && searchData?.queryMessage !== '') &&
                            <div className='font-medium text-[14px] px-3 py-[12px]'>
                                No results found.
                            </div>
                        }

                        {/* Results */}
                        {
                            searchData?.searchResults?.length > 0 &&
                            searchData?.searchResults.map((user, index) => (
                                <div
                                    key={index}
                                    className='flex flex-row justify-between px-4 py-[13px] cursor-pointer rounded-sm hover:bg-[#F0F0F0]'
                                    onClick={() => {
                                        // Save the email address and the name
                                        setValue(dataField, user?.emailaddress);
                                        setValue(`${dataField}givenname`, user?.givenname)
                                        setValue(`${dataField}surname`, user?.surname)
                                        // Clear the errors
                                        clearErrors(dataField);

                                        // Update the search results
                                        setSearchData({
                                            'searchResults': [],
                                            'newUserInput': user?.emailaddress,
                                            'queryMessage' :''
                                        });
                                    }}
                                >

                                    {/* User Detail */}
                                    <div className='flex flex-col'>
                                        <p className='m-0 font-medium text-[14px]'>{user?.givenname} {user?.surname}</p>
                                        <p className='m-0 text-[14px]'>{user?.emailaddress} ({user?.jobtitle})</p>
                                    </div>

                                </div>
                            ))
                        }

                        {/* Total */}
                        <div className='text-sm font-medium px-4 py-2 border-t border-t-[#D8D8D8]' hidden={searchData.queryMessage.length === 0}>
                            {searchData.queryMessage}
                        </div>

                    </div>
                )
            }

            {/* Error Message */}
            {errors && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors?.message}</p>}


        </div>
    )

    //------------------------------------------------------
}
